import { Link, useLocation } from "react-router-dom";
import { Home, Layers, Sparkles, Wallet, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useChat } from "@/contexts/ChatContext";

interface NavItem {
  to?: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  blobShape: string;
  isChat?: boolean;
}

// Same tabs and destinations as the signed-in app's nav (ZTabBar in
// src/redesign/app.jsx) and admin's header, so the legacy pages that still
// render this bar never show a different/old navigation.
const navItems: NavItem[] = [
  {
    to: "/dashboard",
    icon: Home,
    label: "today",
    blobShape: "62% 38% 45% 55% / 48% 58% 42% 52%",
  },
  {
    to: "/app/equipment",
    icon: Layers,
    label: "equipment",
    blobShape: "42% 58% 55% 45% / 55% 42% 58% 45%",
  },
  {
    to: "/bookings",
    icon: Sparkles,
    label: "book",
    blobShape: "58% 42% 45% 55% / 52% 48% 55% 45%",
  },
  {
    to: "/wallet",
    icon: Wallet,
    label: "wallet",
    blobShape: "55% 45% 38% 62% / 45% 55% 48% 52%",
  },
  {
    icon: MessageCircle,
    label: "chat",
    blobShape: "38% 62% 52% 48% / 58% 42% 55% 45%",
    isChat: true,
  },
];

export function BottomNavBar() {
  const location = useLocation();
  const { isOpen, setIsOpen } = useChat();

  const hiddenPaths = [
    "/",
    "/auth",
    "/reset-password",
    "/pricing",
    "/terms",
    // Public guest waiver signing — guests aren't members; no app nav.
    "/guest-terms",
    "/experience",
    // Admin has its own top nav matching the app — don't also show this bar.
    "/admin",
  ];
  const shouldHide = hiddenPaths.some((path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path),
  );

  if (shouldHide) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 safe-area-bottom">
      <div className="relative">
        {/* Seamless curved top edge — organic wave */}
        <svg
          className="absolute -top-4 left-0 right-0 w-full h-5 text-background"
          viewBox="0 0 400 20"
          preserveAspectRatio="none"
          fill="currentColor"
        >
          <path d="M0,20 L0,10 Q100,0 200,6 Q300,12 400,8 L400,20 Z" />
        </svg>
        {/* Subtle moss-green accent line at top */}
        <div className="absolute -top-4 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-zuzu-limed-ash/20 to-transparent z-10" />

        <div className="bg-background/95 backdrop-blur-sm border-t border-border/15">
          <div className="px-6 py-2 pb-3">
            <div className="flex items-center justify-between">
              {navItems.map((item) => {
                const isActive = item.to
                  ? location.pathname === item.to
                  : item.isChat
                    ? isOpen
                    : false;
                const isChat = item.isChat;

                const content = (
                  <div
                    className={cn(
                      "relative flex items-center justify-center transition-all duration-300",
                      isActive
                        ? "scale-105"
                        : "group-hover:scale-105 group-active:scale-110",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute transition-all duration-300",
                        isActive
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-75 group-hover:opacity-50 group-hover:scale-90 group-active:opacity-100 group-active:scale-100",
                      )}
                      style={{
                        background: "hsl(var(--zuzu-coral-tree) / 0.2)",
                        borderRadius: item.blobShape,
                        width: "50px",
                        height: "44px",
                      }}
                    />

                    <div className="relative z-10 flex h-11 w-11 items-center justify-center">
                      <item.icon
                        className={cn(
                          "h-5 w-5 transition-all duration-200",
                          isActive
                            ? "text-primary stroke-[2.5]"
                            : "text-muted-foreground group-hover:text-foreground",
                        )}
                      />
                    </div>
                  </div>
                );

                if (isChat) {
                  return (
                    <button
                      key="chat"
                      type="button"
                      aria-label="open chat"
                      aria-pressed={isOpen}
                      onClick={() => setIsOpen(true)}
                      className="flex flex-col items-center gap-0.5 py-1 group outline-none"
                    >
                      {content}
                      <span
                        className={cn(
                          "text-xs font-medium transition-all duration-200",
                          isActive
                            ? "text-primary font-semibold"
                            : "text-muted-foreground group-hover:text-foreground",
                        )}
                      >
                        {item.label}
                      </span>
                    </button>
                  );
                }

                return (
                  <Link
                    key={item.to}
                    to={item.to!}
                    className="flex flex-col items-center gap-0.5 py-1 group"
                  >
                    {content}
                    <span
                      className={cn(
                        "text-xs font-medium transition-all duration-200",
                        isActive
                          ? "text-primary font-semibold"
                          : "text-muted-foreground group-hover:text-foreground",
                      )}
                    >
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
